import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/Popover";
import FormRow from "./FormRow";
import { usePlaybackSpeed } from "./image-list-item-hooks";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import {
  IconAlertCircle,
  IconCircleCheck,
  IconGauge,
  IconInfoCircle,
  IconReload,
} from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { getAnimatedImageConfig } from "@repo/api/utils/composition";
import SliderInputField from "@/lib/components/SliderInputField";
import { Slider } from "@/lib/components/Slider";
import LoadingSpinner from "@/lib/components/LoadingSpinner";

type Props = {
  timelineItem: CreativeProjectTimelineItem;
  animatedImage: AnimatedImageDto;
};

export default function SlowMotionPopover({
  timelineItem,
  animatedImage,
}: Props) {
  const {
    value: playbackValue,
    minValue: playbackMinValue,
    maxValue: playbackMaxValue,
    handleChange: handlePlaybackChange,
    handleCommit: handlePlaybackCommit,
  } = usePlaybackSpeed(timelineItem, animatedImage);

  const isLoading =
    !["COMPLETED", "FAILED", ""].includes(animatedImage.smStatus ?? "") &&
    playbackValue !== 1;
  const isFailed = animatedImage.smStatus === "FAILED";

  const playbackSpeed = getAnimatedImageConfig(timelineItem).playbackSpeed;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button size="iconXs" variant="outline" className="overflow-hidden">
              {isLoading && (
                <div className="absolute inset-0 animate-pulse-calm bg-primary-400/30"></div>
              )}
              {isFailed ? (
                <IconAlertCircle className="text-error" />
              ) : (
                <IconGauge className="relative" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Playback speed</p>
        </TooltipContent>
      </Tooltip>

      <PopoverContent className="w-112.5 pt-4 pb-5" align="center" side="top">
        <FormRow
          label="Playback speed"
          classNameLabel="w-[120px]"
          classNameContainer="h-8"
        >
          <Slider
            value={playbackValue}
            onValueChange={handlePlaybackChange}
            onValueCommit={handlePlaybackCommit}
            min={playbackMinValue}
            max={playbackMaxValue}
            step={0.05}
          />
          <SliderInputField
            value={playbackValue}
            onCommit={handlePlaybackCommit}
            min={playbackMinValue}
            max={playbackMaxValue}
            decimalPlaces={2}
            className="max-w-12 min-w-12"
            suffix="x"
          />
        </FormRow>
        <div className="mt-0 flex h-8 items-center justify-between">
          <HelperText
            animatedImage={animatedImage}
            playbackSpeed={playbackSpeed}
          />
          <Button
            disabled={playbackValue === 1}
            variant="outline"
            size="iconXs"
            onClick={() => {
              handlePlaybackCommit(1);
            }}
          >
            <IconReload />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function HelperText({
  animatedImage,
  playbackSpeed,
}: {
  animatedImage: AnimatedImageDto;
  playbackSpeed?: number;
}) {
  const isUploadedSpeedChange =
    animatedImage.type === "UPLOADED" &&
    playbackSpeed !== undefined &&
    playbackSpeed !== 1;

  if (
    animatedImage.smStatus === "COMPLETED" ||
    isUploadedSpeedChange ||
    (playbackSpeed && playbackSpeed > 1)
  ) {
    return (
      <div className="flex items-center [&_svg]:stroke-[url(#icon-gradient)]">
        <IconCircleCheck size={16} className="mr-1.5" />
        <p className="text-sm text-text-secondary">Speed change completed</p>
      </div>
    );
  }

  if (animatedImage.smStatus === null) {
    return (
      <div className="flex items-center">
        <IconInfoCircle className="mr-1.5 text-text-secondary" size={16} />
        <p className="text-sm text-text-secondary">
          Changes the speed of the movement
        </p>
      </div>
    );
  }

  if (animatedImage.smStatus === "FAILED") {
    return (
      <div className="flex items-center">
        <IconAlertCircle className="mr-1.5 text-error" size={16} />
        <p className="text-sm text-error">
          Failed to create a smooth speed change
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <LoadingSpinner size={16} className="mr-1.5" />
      <p className="text-sm text-text-secondary">
        Generating smooth speed change (~1 min)
      </p>
    </div>
  );
}
